import React, { useEffect, useRef, useState } from "react";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import Badge from "@material-ui/core/Badge";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import NotificationsNoneOutlinedIcon from "@material-ui/icons/NotificationsNoneOutlined";
import NotificationsActiveOutlinedIcon from "@material-ui/icons/NotificationsActiveOutlined";
import DoneAllIcon from "@material-ui/icons/DoneAll";

import ContactAvatar from "../ContactAvatar";
import useNow from "../../hooks/useNow";
import { replyContactName, useReplyNotifications } from "../../context/ReplyNotifications";
import { isReplyActive, replyCountLabel, replyTimeLabel } from "../../utils/replyNotifications";

export const OPEN_REPLY_NOTIFICATIONS_EVENT = "flytec:open-reply-notifications";

const useStyles = makeStyles(theme => ({
	iconButton: { color: theme.palette.text.secondary },
	paper: {
		width: 380,
		maxWidth: "calc(100vw - 24px)",
		borderRadius: 16,
		border: `1px solid ${theme.custom.border}`,
		boxShadow: "0 18px 50px rgba(15, 23, 42, .18)",
		overflow: "hidden",
	},
	header: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: 12,
		padding: "16px 16px 12px",
		borderBottom: `1px solid ${theme.custom.border}`,
	},
	title: { fontWeight: 800, fontSize: "1rem", letterSpacing: "-0.01em" },
	subtitle: { fontSize: ".74rem", color: theme.palette.text.secondary, marginTop: 2 },
	markAll: { fontSize: ".74rem", whiteSpace: "nowrap", padding: "4px 8px", flexShrink: 0 },
	list: {
		maxHeight: 420,
		overflowY: "auto",
		padding: "6px 0",
		...theme.scrollbarStyles,
	},
	section: {
		padding: "10px 16px 4px",
		fontSize: ".68rem",
		fontWeight: 800,
		letterSpacing: ".06em",
		textTransform: "uppercase",
		color: theme.palette.text.secondary,
	},
	item: {
		display: "flex",
		alignItems: "flex-start",
		gap: 12,
		width: "100%",
		padding: "10px 16px",
		border: 0,
		background: "transparent",
		color: "inherit",
		font: "inherit",
		textAlign: "left",
		cursor: "pointer",
		transition: "background-color .15s ease",
		"&:hover, &:focus-visible": { backgroundColor: theme.custom.surfaceHover, outline: "none" },
	},
	itemActive: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(16,185,129,.10)" : "rgba(16,185,129,.07)",
	},
	avatarWrap: { position: "relative", flexShrink: 0 },
	avatar: { width: 38, height: 38, fontSize: ".85rem" },
	dot: {
		position: "absolute",
		top: -1,
		right: -1,
		width: 11,
		height: 11,
		borderRadius: "50%",
		backgroundColor: theme.palette.primary.main,
		border: `2px solid ${theme.palette.background.paper}`,
	},
	body: { flex: 1, minWidth: 0 },
	row: { display: "flex", alignItems: "baseline", gap: 8 },
	name: {
		flex: 1,
		minWidth: 0,
		fontSize: ".86rem",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	nameActive: { fontWeight: 800 },
	time: { fontSize: ".7rem", color: theme.palette.text.secondary, flexShrink: 0, fontVariantNumeric: "tabular-nums" },
	preview: {
		fontSize: ".8rem",
		lineHeight: 1.45,
		color: theme.palette.text.secondary,
		marginTop: 2,
		display: "-webkit-box",
		WebkitLineClamp: 2,
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
		overflowWrap: "anywhere",
	},
	previewActive: { color: theme.palette.text.primary },
	meta: { display: "flex", alignItems: "center", gap: 6, marginTop: 5 },
	count: {
		fontSize: ".66rem",
		fontWeight: 800,
		color: "#fff",
		backgroundColor: theme.palette.primary.main,
		borderRadius: 999,
		padding: "1px 7px",
	},
	seenLabel: { fontSize: ".68rem", color: theme.palette.text.secondary },
	empty: {
		padding: "36px 24px",
		textAlign: "center",
		color: theme.palette.text.secondary,
		fontSize: ".85rem",
		"& svg": { fontSize: 36, opacity: 0.5, marginBottom: 8 },
	},
	footer: {
		padding: "10px 16px 12px",
		borderTop: `1px solid ${theme.custom.border}`,
		fontSize: ".7rem",
		color: theme.palette.text.secondary,
		textAlign: "center",
	},
}));

const NotificationItem = ({ notification, now, onOpen, classes }) => {
	const active = isReplyActive(notification);
	const name = replyContactName(notification);
	const count = replyCountLabel(notification);
	return (
		<button
			type="button"
			className={`${classes.item} ${active ? classes.itemActive : ""}`}
			onClick={() => onOpen(notification)}
			aria-label={`${name} respondeu${active ? ", não visualizada" : ""}. Abrir conversa`}
		>
			<div className={classes.avatarWrap}>
				<ContactAvatar
					contact={notification.contact || { name: notification.contactName }}
					className={classes.avatar}
				/>
				{active && <span className={classes.dot} aria-hidden="true" />}
			</div>
			<div className={classes.body}>
				<div className={classes.row}>
					<Typography component="span" className={`${classes.name} ${active ? classes.nameActive : ""}`}>
						{name}
					</Typography>
					<span className={classes.time}>{replyTimeLabel(notification.notifiedAt, now)}</span>
				</div>
				<div className={`${classes.preview} ${active ? classes.previewActive : ""}`}>
					{notification.lastBody || "Nova mensagem"}
				</div>
				{(count || !active) && (
					<div className={classes.meta}>
						{count && <span className={classes.count}>{count}</span>}
						{!active && <span className={classes.seenLabel}>Visualizada</span>}
					</div>
				)}
			</div>
		</button>
	);
};

const NotificationsPopOver = () => {
	const classes = useStyles();
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const { notifications, activeCount, openNotification, markAllSeen } = useReplyNotifications();
	const now = useNow(isOpen);

	useEffect(() => {
		const open = () => setIsOpen(true);
		window.addEventListener(OPEN_REPLY_NOTIFICATIONS_EVENT, open);
		return () => window.removeEventListener(OPEN_REPLY_NOTIFICATIONS_EVENT, open);
	}, []);

	const active = notifications.filter(isReplyActive);
	const seen = notifications.filter(item => !isReplyActive(item));

	const handleOpen = notification => {
		setIsOpen(false);
		openNotification(notification);
	};

	const tooltip = activeCount
		? `${activeCount} ${activeCount === 1 ? "resposta não visualizada" : "respostas não visualizadas"}`
		: "Notificações";

	return (
		<>
			<Tooltip title={tooltip}>
				<IconButton
					onClick={() => setIsOpen(previous => !previous)}
					ref={anchorEl}
					aria-label={`Notificações de respostas, ${activeCount} não visualizadas`}
					className={classes.iconButton}
				>
					<Badge badgeContent={activeCount} color="secondary" max={99}>
						{activeCount ? <NotificationsActiveOutlinedIcon /> : <NotificationsNoneOutlinedIcon />}
					</Badge>
				</IconButton>
			</Tooltip>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				classes={{ paper: classes.paper }}
				onClose={() => setIsOpen(false)}
			>
				<div className={classes.header}>
					<div>
						<Typography className={classes.title}>Notificações</Typography>
						<Typography className={classes.subtitle}>Respostas dos clientes que você atende</Typography>
					</div>
					<Button
						size="small"
						color="primary"
						title="Marcar todas como visualizadas"
						className={classes.markAll}
						startIcon={<DoneAllIcon fontSize="small" />}
						disabled={!activeCount}
						onClick={markAllSeen}
					>
						Marcar todas
					</Button>
				</div>
				<div className={classes.list}>
					{notifications.length === 0 ? (
						<div className={classes.empty}>
							<NotificationsNoneOutlinedIcon />
							<div>Nenhuma resposta nas últimas 24 horas.</div>
						</div>
					) : (
						<>
							{active.length > 0 && <div className={classes.section}>Não visualizadas</div>}
							{active.map(notification => (
								<NotificationItem key={notification.id} notification={notification} now={now} onOpen={handleOpen} classes={classes} />
							))}
							{seen.length > 0 && <div className={classes.section}>Visualizadas</div>}
							{seen.map(notification => (
								<NotificationItem key={notification.id} notification={notification} now={now} onOpen={handleOpen} classes={classes} />
							))}
						</>
					)}
				</div>
				<div className={classes.footer}>O histórico guarda as notificações por 24 horas.</div>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
